// @ts-strict-ignore
import { hasMobileUserAgent } from '@carvana/stc-utilities/detectMobile/detectMobile';
import logError from './logError';

export interface WebViewMessage<T = unknown> {
  event: string;
  payload: T;
}

export type IOSPayload = {
  path?: string;
  category?: string;
  caption?: string;
  offerToken?: string;
  forecastId?: string;
  appraisalId?: string;
  requiresAuth?: boolean;
  ecoVehicleToken?: string;
};

export interface IOSMessagingOptions {
  pageName?: string;
  actionName?: string;
  trackAnalytics?: boolean;
  setIsLoading?: (isLoading: boolean) => void;
  recordActionClicked?: (
    pageName: string,
    actionName: string,
    payload: Record<string, unknown>,
    sendImmediately?: boolean
  ) => void;
}

export const createWebViewMessage = <T>(event: string, payload: T): WebViewMessage<T> => ({
  event,
  payload
});

/**
 *
 * @param agent User agent string to check (server side)
 * @returns if is android webview
 */
export const isAndroidWebView = (agent: string): boolean =>
  /CarvanaNativeWebView/gi.test(agent) && /Android/gi.test(agent);

/**
 *
 * @param agent User agent string to check (server side)
 * @returns if is ios webview
 */
export const isIOSWebViewV2 = (agent: string): boolean =>
  /CarvanaNativeWebView/gi.test(agent) && /iPhone|iPad/gi.test(agent);

/**
 * Checks if current environment is iOS WebView
 */
export const isIOSWebView = (): boolean =>
  typeof window !== 'undefined' &&
  hasMobileUserAgent(window.navigator) &&
  !!window?.webkit?.messageHandlers;

/**
 * Type definition for WebKit message handlers
 */
interface WebkitMessageHandlers {
  nativeWebViewMessageHandler?: {
    postMessage: (message: string) => void;
  };
}

/**
 * Sends a message to iOS WebView
 * This is an internal function that doesn't check if we're in iOS WebView
 */
export const sendToIOS = <T>(event: string, payload: T): void => {
  if (typeof window === 'undefined') {
    return;
  }

  const message = JSON.stringify(createWebViewMessage(event, payload));

  try {
    const handlers = window?.webkit?.messageHandlers as WebkitMessageHandlers;
    handlers?.nativeWebViewMessageHandler?.postMessage(message);
  } catch (e) {
    const error = new Error('Error sending postMessage to WebView') as Error & { context?: unknown };
    error.context = { event };
    if (e instanceof Error) {
      error.stack = e.stack;
    }
    logError(error);
  }
};

/**
 * Sends an event to iOS with analytics tracking and loading state management
 * This is the recommended method for communicating with iOS from web views
 */
export const sendEventToIOS = (
  eventName: string,
  payload: IOSPayload = {},
  options: IOSMessagingOptions = {}
): void => {
  const {
    pageName,
    actionName,
    trackAnalytics = true,
    setIsLoading,
    recordActionClicked
  } = options;

  const isIOS = isIOSWebView();

  if (isIOS) {
    if (trackAnalytics && pageName && actionName) {
      recordActionClicked?.(pageName, actionName, payload, true);
    }

    sendToIOS(eventName, payload);
  }

  if (setIsLoading) {
    setIsLoading(false);
  }
};
